import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, CornerUpLeft, Pencil, Plus, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader } from '@/components/app/PageHeader';
import { EmptyState, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { EditedMark, ExampleDataNote, MiniStat, RefsErrorNote, TimeStatusPill, useDriftRefs } from '@/components/app/DriftUI';
import { DriftFilterBar, useDriftFilters } from '@/components/app/DriftFilters';
import { ReturnReasonDialog, SupplementChips, TimeEntryCard, TimeEntrySheet } from '@/components/app/TimeEntrySheet';
import { TIME_STATUS_LABELS, getTimeEntry, hoursText, listMachines, listTimeEntries, patchTimeEntry } from '@/lib/driftApi';
import { useDemoProfile } from '@/state/profile';
import { formatDateShort } from '@/domain/time';
import { fmtNumber } from '@/domain/numbers';
import { cn } from '@/lib/utils';

/*
  Timer (kontor): samlet oversikt over de samme føringene som medarbeiderne ser i «Min dag». Kontoret godkjenner,
  eller sender tilbake med en kort beskjed – alt lagres i føringens historikk. Kunde vises alltid ved siden av prosjekt.
  Status «utkast / sendt / returnert / godkjent» er en forenklet eksempelstatus – ikke kundens lønns- eller godkjenningsregler,
  og det finnes ingen satser i eksempeldataene. Filtre ligger i URL-en (periode + prosjekt primært, resten under «Flere filtre»).
*/

const FILTER_KEYS = ['projectId', 'subprojectId', 'employeeId', 'machineId', 'status', 'activityId', 'wageTypeId'];
const MORE_KEYS = ['subprojectId', 'employeeId', 'machineId', 'status', 'activityId', 'wageTypeId'];
const STATUS_ORDER = ['sendt', 'returnert', 'utkast', 'godkjent'];

export default function TimerPage() {
  const { refs, error: refsError, loading: refsLoading, retry: retryRefs } = useDriftRefs();
  const { employeeId: profileEmployeeId } = useDemoProfile();
  const f = useDriftFilters({ keys: FILTER_KEYS });
  const { today } = f;
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [sheet, setSheet] = useState(null); // null | { entry } | { initial }
  const [returning, setReturning] = useState(null); // føring som returneres fra raden
  const [busyId, setBusyId] = useState(null);
  const [machineList, setMachineList] = useState([]);
  const handledUrl = useRef({ ny: false, rediger: null });

  const loadMachines = useCallback(() => {
    listMachines()
      .then((d) => setMachineList(d.machines))
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.title = 'Timer · Hardanger Rock';
    loadMachines();
  }, [loadMachines]);

  const queryKey = JSON.stringify(f.query);
  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      const d = await listTimeEntries(JSON.parse(queryKey));
      setData(d);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  const activeProject = f.values.projectId !== 'alle' ? f.values.projectId : '';
  const activeSub = f.values.subprojectId !== 'alle' ? f.values.subprojectId : '';
  const activeEmployee = f.values.employeeId !== 'alle' ? f.values.employeeId : '';

  /** Ny føring fra kontoret: prosjekt (og underprosjekt) forhåndsvalgt fra aktivt filter – aldri blankt når man står i et prosjekt. */
  const openNew = useCallback(() => {
    const project = refs?.projects.find((p) => p.id === activeProject);
    setSheet({ initial: { employeeId: activeEmployee || profileEmployeeId || '', projectId: activeProject, subprojectId: activeSub, customerId: project?.customerId ?? '' } });
  }, [refs, activeProject, activeSub, activeEmployee, profileEmployeeId]);

  const openEdit = useCallback((entry) => setSheet({ entry }), []);

  // ?ny=1 → åpne skjema (prosjekt fra filter). ?rediger=<id> → åpne føringen.
  useEffect(() => {
    if (f.params.get('ny') && !handledUrl.current.ny) {
      handledUrl.current.ny = true;
      openNew();
      f.remove('ny');
    }
    const id = f.params.get('rediger');
    if (id && handledUrl.current.rediger !== id) {
      handledUrl.current.rediger = id;
      getTimeEntry(id)
        .then((e) => openEdit(e))
        .catch(() => {
          toast.error('Fant ikke timeføringen', { description: 'Den kan være slettet.' });
          f.remove('rediger');
        });
    }
  }, [f, openNew, openEdit]);

  const closeSheet = () => {
    setSheet(null);
    if (f.params.get('rediger')) {
      handledUrl.current.rediger = null;
      f.remove('rediger');
    }
  };

  /** «Prøv igjen» henter både timeføringer og referansedata (prosjekter, medarbeidere). */
  const retryAll = () => {
    load();
    if (!refs) retryRefs();
    if (!machineList.length) loadMachines();
  };

  /** Kontorhandlinger rett fra raden. Retur krever beskjed (dialog). */
  const setEntryStatus = async (entry, to, reason) => {
    setBusyId(entry.id);
    try {
      const updated = await patchTimeEntry(entry.id, { status: to, reason: reason || undefined });
      toast.success({ godkjent: 'Godkjent', returnert: 'Returnert med beskjed', sendt: 'Gjenåpnet – kan rettes igjen' }[to], { description: `${formatDateShort(entry.date)} · ${entry.employee?.name} · ${hoursText(entry.hours)}` });
      if (sheet?.entry?.id === entry.id) setSheet({ entry: updated });
      load();
    } catch (err) {
      toast.error('Kunne ikke endre status', { description: err.message });
    } finally {
      setBusyId(null);
    }
  };

  const sums = data?.sums;
  const entries = data?.entries ?? [];
  const byProject = useMemo(() => (refs && sums ? Object.entries(sums.byProject).map(([id, h]) => ({ id, name: refs.projects.find((p) => p.id === id)?.name ?? id, customer: refs.projects.find((p) => p.id === id)?.customer, hours: h })).sort((a, b) => b.hours - a.hours) : []), [refs, sums]);
  const supplementList = sums ? Object.values(sums.supplements) : [];
  const projectName = activeProject ? refs?.projects?.find((p) => p.id === activeProject)?.name : null;
  const statusFilter = f.values.status === 'alle' ? '' : f.values.status;

  return (
    <div className="space-y-5">
      <PageHeader title="Timer" lead="Kontorets samlede oversikt – samme føringer som medarbeiderne ser i Min dag. Godkjenn, eller send tilbake med en kort beskjed.">
        <Button onClick={openNew} className="h-11 px-4 font-semibold lg:h-10" data-testid="timer-new-button">
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Registrer timer
        </Button>
      </PageHeader>

      <ExampleDataNote>Status «utkast / sendt / returnert / godkjent» er en forenklet eksempelstatus, ikke kundens lønns- eller godkjenningsregler – og ingen satser er lagt inn.</ExampleDataNote>

      <Panel>
        <div className="grid grid-cols-2 divide-x divide-[var(--hr-hairline)] lg:grid-cols-4" data-testid="timer-stats">
          <MiniStat label="Timer i perioden" value={sums ? hoursText(sums.hours) : '–'} meta={sums ? `${entries.length} ${entries.length === 1 ? 'føring' : 'føringer'} · ${Object.keys(sums.byEmployee).length} medarbeidere` : ''} testId="timer-stat-hours" />
          <MiniStat label="Maskintimer" value={sums ? hoursText(sums.machineHours) : '–'} meta={sums ? `${Object.keys(sums.byMachine).length} maskiner` : ''} testId="timer-stat-machine" />
          <MiniStat label="Tillegg" value={supplementList.length ? supplementList.map((s) => `${fmtNumber(s.quantity, s.unit === 'stk' ? 0 : 1)} ${s.unit}`).join(' · ') : '–'} meta={supplementList.map((s) => s.label).join(' · ') || 'Ingen tillegg'} testId="timer-stat-supplements" />
          <button type="button" onClick={() => f.set('status', statusFilter === 'sendt' ? 'alle' : 'sendt')} className="text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" aria-pressed={statusFilter === 'sendt'} title="Vis kun føringer som venter på kontoret" data-testid="timer-stat-pending-button">
            <MiniStat label="Til godkjenning" value={sums ? fmtNumber(sums.byStatus.sendt ?? 0, 0) : '–'} meta={sums ? `${sums.byStatus.returnert ?? 0} returnert · ${sums.byStatus.godkjent ?? 0} godkjent` : ''} tone={sums?.byStatus?.sendt ? 'amber' : undefined} testId="timer-stat-pending" />
          </button>
        </div>
      </Panel>

      <Panel className="min-w-0">
        <PanelHeader
          title="Timeoversikt"
          description={projectName ? `Viser føringer på ${projectName}.` : 'Periode og prosjekt først – øvrige valg under «Flere filtre».'}
          actions={
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Status" data-testid="timer-status-chips">
              <button type="button" onClick={() => f.set('status', 'alle')} aria-pressed={!statusFilter} className={cn('h-8 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', !statusFilter ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]')} data-testid="timer-status-chip-alle">
                Alle{sums ? ` ${sums.count}` : ''}
              </button>
              {STATUS_ORDER.map((s) => (
                <button key={s} type="button" onClick={() => f.set('status', statusFilter === s ? 'alle' : s)} aria-pressed={statusFilter === s} className={cn('h-8 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', statusFilter === s ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]')} data-testid={`timer-status-chip-${s}`}>
                  {TIME_STATUS_LABELS[s]}{sums && !statusFilter ? ` ${sums.byStatus[s] ?? 0}` : ''}
                </button>
              ))}
              {entries.length > 0 && <span className="hr-tabular ml-1 text-[13px] text-[var(--hr-muted)]" data-testid="timer-count">{entries.length} {entries.length === 1 ? 'føring' : 'føringer'}</span>}
            </div>
          }
        />
        <DriftFilterBar f={f} refs={refs} machines={machineList} more={MORE_KEYS} testPrefix="timer" />
        {refsError && !refs && <div className="px-5 pt-5"><RefsErrorNote error={refsError} loading={refsLoading} onRetry={retryRefs} what="prosjekter og medarbeidere" testId="timer-refs-error" /></div>}
        {status === 'loading' && <div className="p-5"><SkeletonTable rows={6} /></div>}
        {status === 'error' && <div className="p-5"><Note tone="amber" data-testid="timer-load-error">Kunne ikke hente timeføringer. <Button variant="link" className="h-auto px-1" onClick={retryAll} data-testid="timer-load-retry">Prøv igjen</Button></Note></div>}
        {(status === 'ready' || status === 'refreshing') && entries.length === 0 && (
          <div className="p-5">
            <EmptyState title="Ingen timeføringer i utvalget" description="Endre periode, status eller filter – eller registrer timer." onReset={f.activeCount || f.period.preset !== '14' ? f.reset : undefined} action={<Button onClick={openNew} className="rounded-[10px]" data-testid="timer-empty-new">Registrer timer</Button>} />
          </div>
        )}
        {(status === 'ready' || status === 'refreshing') && entries.length > 0 && (
          <>
            {/* Mobil/nettbrett: samme kort som i Min dag, med medarbeider og dato */}
            <ul className="space-y-3 p-4 lg:hidden" data-testid="timer-list-mobile">
              {entries.map((e) => (
                <TimeEntryCard key={e.id} entry={e} onOpen={openEdit} showEmployee showDate testPrefix="timer-mobile" />
              ))}
            </ul>
            {/* Desktop: tabell med intern rulling – aldri sidescroll på siden */}
            <div className="hr-scroll hidden overflow-x-auto lg:block" data-testid="timer-table-scroll">
              <Table data-testid="timer-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Dato og tid</TableHead>
                    <TableHead>Medarbeider</TableHead>
                    <TableHead className="hidden xl:table-cell">Kunde</TableHead>
                    <TableHead>Prosjekt</TableHead>
                    <TableHead>Aktivitet</TableHead>
                    <TableHead className="text-right">Timer</TableHead>
                    <TableHead className="hidden 2xl:table-cell">Tillegg</TableHead>
                    <TableHead className="hidden xl:table-cell">Maskin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[1%]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id} className={cn(e.status === 'returnert' && 'bg-[var(--hr-amber-bg)]/40')} data-testid={`timer-row-${e.id}`}>
                      <TableCell className="hr-tabular whitespace-nowrap">
                        <button type="button" onClick={() => openEdit(e)} className="rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`timer-open-${e.id}`}>
                          {formatDateShort(e.date)}
                        </button>
                        <div className="text-[13px] text-[var(--hr-muted)]">{e.fromTime}–{e.toTime}{e.breakMinutes ? ` · ${e.breakMinutes} min` : ''}</div>
                        {e.edited && <div className="mt-0.5"><EditedMark count={e.history.filter((h) => h.kind === 'endret').length} testId={`timer-edited-${e.id}`} /></div>}
                      </TableCell>
                      <TableCell className="font-medium text-[var(--hr-heading)]">
                        <button type="button" onClick={() => f.set('employeeId', e.employeeId)} className="rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" title="Filtrer på medarbeideren" data-testid={`timer-employee-link-${e.id}`}>
                          {e.employee?.name}
                        </button>
                      </TableCell>
                      <TableCell className="hidden max-w-[180px] truncate xl:table-cell" title={e.project?.customer} data-testid={`timer-customer-${e.id}`}>{e.project?.customer}</TableCell>
                      <TableCell>
                        <button type="button" onClick={() => f.set('projectId', e.projectId)} className="rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" title="Filtrer på prosjektet" data-testid={`timer-project-link-${e.id}`}>
                          {e.project?.name}
                        </button>
                        {e.subproject && <div className="text-[13px] text-[var(--hr-muted)]">{e.subproject.name}</div>}
                        {e.project?.customer && <div className="max-w-[200px] truncate text-[13px] text-[var(--hr-muted)] xl:hidden" title={e.project.customer}>{e.project.customer}</div>}
                      </TableCell>
                      <TableCell>
                        {e.activity?.label}
                        {e.wageType && e.wageTypeId !== 'ordinaer' && <div className="text-[13px] text-[var(--hr-muted)]">{e.wageType.label}</div>}
                      </TableCell>
                      <TableCell className="hr-tabular text-right font-semibold text-[var(--hr-heading)]">
                        {fmtNumber(e.hours, 2)}
                        {/* lg (1024–1279): maskin foldes under timetallet – egen kolonne vises fra xl */}
                        {e.machine && (
                          <div className="mt-0.5 whitespace-nowrap text-[12px] font-medium leading-4 text-[var(--hr-accent-text)] xl:hidden" data-testid={`timer-machine-inline-${e.id}`}>
                            {e.machine.internalNo} · {hoursText(e.machineTotalHours)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden 2xl:table-cell">{e.supplements?.length ? <SupplementChips list={e.supplements} /> : <span className="text-[var(--hr-muted-2)]">–</span>}</TableCell>
                      <TableCell className="hidden xl:table-cell">
                        {e.machine ? (
                          <Link to={`/maskiner/${e.machine.machineId}`} className="whitespace-nowrap font-medium text-[var(--hr-accent-text)] hover:underline" data-testid={`timer-machine-link-${e.id}`}>
                            {e.machine.internalNo} · {hoursText(e.machineTotalHours)}
                          </Link>
                        ) : (
                          <span className="text-[var(--hr-muted-2)]">–</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <TimeStatusPill status={e.status} data-testid={`timer-status-${e.id}`} />
                        {e.status === 'returnert' && e.returnNote?.text && <div className="mt-1 hidden max-w-[170px] truncate text-[12px] text-[var(--hr-amber)] xl:block" title={e.returnNote.text}>«{e.returnNote.text}»</div>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center justify-end gap-0.5">
                          {e.status === 'sendt' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => setEntryStatus(e, 'godkjent')} disabled={busyId === e.id} aria-label="Godkjenn" title="Godkjenn (eksempelstatus)" data-testid={`timer-approve-${e.id}`}>
                                <CheckCircle2 className="h-4 w-4 text-[var(--hr-green)]" aria-hidden="true" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => setReturning(e)} disabled={busyId === e.id} aria-label="Returner med beskjed" title="Returner med beskjed" data-testid={`timer-return-${e.id}`}>
                                <CornerUpLeft className="h-4 w-4 text-[var(--hr-amber)]" aria-hidden="true" />
                              </Button>
                            </>
                          )}
                          {e.status === 'godkjent' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => setEntryStatus(e, 'sendt', 'Gjenåpnet av kontoret')} disabled={busyId === e.id} aria-label="Gjenåpne" title="Gjenåpne for retting" data-testid={`timer-reopen-${e.id}`}>
                              <RotateCcw className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => openEdit(e)} aria-label="Åpne timeføring" title="Åpne" data-testid={`timer-edit-${e.id}`}>
                            <Pencil className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Panel>

      {byProject.length > 0 && (
        <Panel>
          <PanelHeader title="Timer per kunde og prosjekt" description="Summer for valgt utvalg. Åpne prosjektet for maskintimer, tillegg og vareforbruk." />
          <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="timer-by-project">
            {byProject.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 px-5 py-2.5 text-[14px]">
                <div className="min-w-0">
                  <Link to={`/prosjekter/${p.id}${f.carry({}, { only: [] })}`} className="block truncate font-medium text-[var(--hr-heading)] hover:underline" data-testid={`timer-project-${p.id}`}>
                    {p.name}
                  </Link>
                  {p.customer && <div className="truncate text-[13px] text-[var(--hr-muted)]">{p.customer}</div>}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="hr-num font-semibold">{hoursText(p.hours)}</span>
                  <button type="button" onClick={() => f.set('projectId', p.id)} className="text-[13px] text-[var(--hr-muted)] hover:text-[var(--hr-heading)] hover:underline" data-testid={`timer-project-filter-${p.id}`}>
                    Vis føringer
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <TimeEntrySheet open={!!sheet} onClose={closeSheet} entry={sheet?.entry ?? null} initial={sheet?.initial} context="office" today={today} onChanged={() => load()} />
      <ReturnReasonDialog open={!!returning} onOpenChange={(o) => !o && setReturning(null)} entry={returning} busy={busyId === returning?.id} onConfirm={(reason) => setEntryStatus(returning, 'returnert', reason)} />
    </div>
  );
}
